<div class="admin-container">
  <!-- Header Section -->
  <div class="admin-header">
    <h1 class="admin-title">
      <mat-icon class="admin-icon">admin_panel_settings</mat-icon>
      Admin Dashboard
    </h1>
    <p class="admin-subtitle">Manage companies and system settings</p>
  </div>

  <!-- Stats Cards -->
  <div class="stats-grid">
    <mat-card class="stat-card">
      <mat-card-content>
        <div class="stat-content">
          <mat-icon class="stat-icon">business</mat-icon>
          <div class="stat-info">
            <h3>{{totalCompanies}}</h3>
            <p>Total Companies</p>
          </div>
        </div>
      </mat-card-content>
    </mat-card>

    <mat-card class="stat-card">
      <mat-card-content>
        <div class="stat-content">
          <mat-icon class="stat-icon">event_available</mat-icon>
          <div class="stat-info">
            <h3>{{totalBookings}}</h3>
            <p>Total Bookings</p>
          </div>
        </div>
      </mat-card-content>
    </mat-card>

    <mat-card class="stat-card">
      <mat-card-content>
        <div class="stat-content">
          <mat-icon class="stat-icon">hotel</mat-icon>
          <div class="stat-info">
            <h3>{{totalHotelPartners}}</h3>
            <p>Hotel Partners</p>
          </div>
        </div>
      </mat-card-content>
    </mat-card>
  </div>

  <!-- Main Content Tabs -->
  <mat-tab-group class="admin-tabs" (selectedTabChange)="onTabChange($event)">
    <!-- Company Management Tab -->
    <mat-tab label="Company Management">
      <div class="tab-content">
        <!-- Add Company Form -->
        <mat-card class="form-card">
          <mat-card-header>
            <mat-card-title>
              <mat-icon>add_business</mat-icon>
              Add New Company
            </mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <form (ngSubmit)="onSubmit()" [formGroup]="companySetupForm" class="sleek-form">
              <!-- Company Information Section -->
              <div class="form-section">
                <h3 class="section-title">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                    <polyline points="9 22 9 12 15 12 15 22"></polyline>
                  </svg>
                  Company Information
                </h3>

                <div class="form-grid">
                  <div class="form-field">
                    <label>Company Name <span class="required">*</span></label>
                    <input type="text" formControlName="companyName" 
                           class="form-input" 
                           [class.invalid]="f.companyName.invalid && f.companyName.touched"
                           placeholder="Enter company name">
                    <span class="error-message" *ngIf="f.companyName.invalid && f.companyName.touched">
                      Company name is required (min 2 characters)
                    </span>
                  </div>

                  <div class="form-field">
                    <label>Contact Email <span class="required">*</span></label>
                    <input type="email" formControlName="companyEmail" 
                           class="form-input"
                           [class.invalid]="f.companyEmail.invalid && f.companyEmail.touched"
                           placeholder="company@example.com">
                    <span class="error-message" *ngIf="f.companyEmail.invalid && f.companyEmail.touched">
                      Valid email is required
                    </span>
                  </div>

                  <div class="form-field">
                    <label>Contact Name <span class="required">*</span></label>
                    <input type="text" formControlName="companyContactName" 
                           class="form-input"
                           [class.invalid]="f.companyContactName.invalid && f.companyContactName.touched"
                           placeholder="John Doe">
                    <span class="error-message" *ngIf="f.companyContactName.invalid && f.companyContactName.touched">
                      Contact name is required (min 2 characters)
                    </span>
                  </div>

                  <div class="form-field">
                    <label>Phone Number <span class="required">*</span></label>
                    <input type="tel" formControlName="phone" 
                           class="form-input"
                           [class.invalid]="f.phone.invalid && f.phone.touched"
                           placeholder="+1 (555) 123-4567">
                    <span class="error-message" *ngIf="f.phone.invalid && f.phone.touched">
                      Phone number is required
                    </span>
                  </div>

                  <div class="form-field full-width">
                    <label>Company Address <span class="required">*</span></label>
                    <input type="text" formControlName="companyAddress" 
                           class="form-input"
                           [class.invalid]="f.companyAddress.invalid && f.companyAddress.touched"
                           placeholder="123 Main St, City, State, ZIP">
                    <span class="error-message" *ngIf="f.companyAddress.invalid && f.companyAddress.touched">
                      Company address is required
                    </span>
                  </div>

                  <div class="form-field full-width">
                    <label>Website <span class="required">*</span></label>
                    <input type="url" formControlName="companyWebsite" 
                           class="form-input"
                           [class.invalid]="f.companyWebsite.invalid && f.companyWebsite.touched"
                           placeholder="https://www.company.com">
                    <span class="error-message" *ngIf="f.companyWebsite.invalid && f.companyWebsite.touched">
                      Valid website URL is required (e.g., https://www.example.com)
                    </span>
                  </div>

                  <div class="form-field full-width">
                    <label>Company Description <span class="required">*</span></label>
                    <textarea formControlName="companyDescription" 
                              class="form-input form-textarea"
                              [class.invalid]="f.companyDescription.invalid && f.companyDescription.touched"
                              rows="4"
                              maxlength="500"
                              placeholder="Describe the company's services and offerings..."></textarea>
                    <div class="field-footer">
                      <span class="error-message" *ngIf="f.companyDescription.invalid && f.companyDescription.touched">
                        Description is required (min 10 characters)
                      </span>
                      <span class="char-count">{{f.companyDescription.value?.length || 0}} / 500</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- User Account Section -->
              <div class="form-section">
                <h3 class="section-title">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                  Company Admin User Account
                </h3>
                <p class="section-description">Create a user account for the company administrator</p>

                <div class="form-grid">
                  <div class="form-field">
                    <label>Admin Email <span class="required">*</span></label>
                    <input type="email" formControlName="userEmail" 
                           class="form-input"
                           [class.invalid]="f.userEmail.invalid && f.userEmail.touched"
                           placeholder="admin@company.com">
                    <span class="error-message" *ngIf="f.userEmail.invalid && f.userEmail.touched">
                      Valid email is required
                    </span>
                  </div>

                  <div class="form-field">
                    <label>Password <span class="required">*</span></label>
                    <input type="password" formControlName="userPassword" 
                           class="form-input"
                           [class.invalid]="f.userPassword.invalid && f.userPassword.touched"
                           placeholder="Minimum 8 characters">
                    <span class="error-message" *ngIf="f.userPassword.invalid && f.userPassword.touched">
                      Password must be at least 8 characters
                    </span>
                  </div>

                  <div class="form-field">
                    <label>First Name <span class="required">*</span></label>
                    <input type="text" formControlName="userFirstName" 
                           class="form-input"
                           [class.invalid]="f.userFirstName.invalid && f.userFirstName.touched"
                           placeholder="John">
                    <span class="error-message" *ngIf="f.userFirstName.invalid && f.userFirstName.touched">
                      First name is required (min 2 characters)
                    </span>
                  </div>

                  <div class="form-field">
                    <label>Last Name <span class="required">*</span></label>
                    <input type="text" formControlName="userLastName" 
                           class="form-input"
                           [class.invalid]="f.userLastName.invalid && f.userLastName.touched"
                           placeholder="Doe">
                    <span class="error-message" *ngIf="f.userLastName.invalid && f.userLastName.touched">
                      Last name is required (min 2 characters)
                    </span>
                  </div>

                  <div class="form-field">
                    <label>Phone Number <span class="required">*</span></label>
                    <input type="tel" formControlName="userPhoneNumber" 
                           class="form-input"
                           [class.invalid]="f.userPhoneNumber.invalid && f.userPhoneNumber.touched"
                           placeholder="+1 (555) 123-4567">
                    <span class="error-message" *ngIf="f.userPhoneNumber.invalid && f.userPhoneNumber.touched">
                      Phone number is required
                    </span>
                  </div>
                </div>
              </div>

              <div class="form-actions">
                <button type="submit" 
                        class="btn btn-primary" 
                        [disabled]="companySetupForm.invalid || isLoading">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                    <polyline points="17 21 17 13 7 13 7 21"></polyline>
                    <polyline points="7 3 7 8 15 8"></polyline>
                  </svg>
                  {{isLoading ? 'Creating...' : 'Create Company'}}
                </button>
                <button type="button" class="btn btn-secondary" (click)="resetForm()">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="23 4 23 10 17 10"></polyline>
                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                  </svg>
                  Reset Form
                </button>
              </div>
            </form>
          </mat-card-content>
        </mat-card>

        <!-- Companies List -->
        <mat-card class="list-card">
          <mat-card-header>
            <mat-card-title>
              <mat-icon>business</mat-icon>
              Company Directory
            </mat-card-title>
            <div class="header-actions">
              <mat-form-field appearance="outline" class="search-field">
                <mat-label>Search Companies</mat-label>
                <input matInput (keyup)="applyFilter($event)" placeholder="Filter by name, email..." #input>
                <mat-icon matSuffix>search</mat-icon>
              </mat-form-field>
            </div>
          </mat-card-header>
          <mat-card-content>
            <div class="table-container">
              <table mat-table [dataSource]="dataSource" matSort class="admin-table">
                <ng-container matColumnDef="id">
                  <th mat-header-cell *matHeaderCellDef mat-sort-header>ID</th>
                  <td mat-cell *matCellDef="let row">
                    <a [routerLink]="['/backoffice/all-stores']" [queryParams]="{companyId: row.id}" class="id-link" (click)="navigateToCompanyDashboard(row)">
                      {{row.id}}
                    </a>
                  </td>
                </ng-container>

                <ng-container matColumnDef="companyName">
                  <th mat-header-cell *matHeaderCellDef mat-sort-header>Company Name</th>
                  <td mat-cell *matCellDef="let row">
                    <div class="company-cell">
                      <strong>{{row.companyName || 'N/A'}}</strong>
                    </div>
                  </td>
                </ng-container>

                <ng-container matColumnDef="companyContactName">
                  <th mat-header-cell *matHeaderCellDef mat-sort-header>Contact</th>
                  <td mat-cell *matCellDef="let row">{{row.companyContactName || 'N/A'}}</td>
                </ng-container>

                <ng-container matColumnDef="companyContactEmail">
                  <th mat-header-cell *matHeaderCellDef mat-sort-header>Email</th>
                  <td mat-cell *matCellDef="let row">
                    <a [href]="'mailto:' + (row.companyContactEmail || '')" class="email-link">
                      {{row.companyContactEmail || 'N/A'}}
                    </a>
                  </td>
                </ng-container>

                <ng-container matColumnDef="companyDescription">
                  <th mat-header-cell *matHeaderCellDef mat-sort-header>Description</th>
                  <td mat-cell *matCellDef="let row">
                    <div class="description-cell" [title]="row.companyDescription || ''">
                      {{(row.companyDescription || '') | slice:0:50}}{{row.companyDescription?.length > 50 ? '...' : ''}}
                    </div>
                  </td>
                </ng-container>

                <ng-container matColumnDef="actions">
                  <th mat-header-cell *matHeaderCellDef>Actions</th>
                  <td mat-cell *matCellDef="let row">
                    <button mat-icon-button [matMenuTriggerFor]="actionMenu" 
                            (click)="selectedCompany = row">
                      <mat-icon>more_vert</mat-icon>
                    </button>
                    <mat-menu #actionMenu="matMenu">
                      <button mat-menu-item (click)="editCompany(row)">
                        <mat-icon>edit</mat-icon>
                        Edit
                      </button>
                      <button mat-menu-item (click)="viewCompanyDetails(row)">
                        <mat-icon>visibility</mat-icon>
                        View Details
                      </button>
                      <button mat-menu-item (click)="deleteCompany(row)" class="delete-action">
                        <mat-icon>delete</mat-icon>
                        Delete
                      </button>
                    </mat-menu>
                  </td>
                </ng-container>

                <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
                <tr mat-row *matRowDef="let row; columns: displayedColumns;" 
                    class="table-row" (click)="viewCompanyDetails(row)"></tr>

                <tr class="mat-row" *matNoDataRow>
                  <td class="mat-cell no-data" [attr.colspan]="displayedColumns.length">
                    <div class="no-data-content">
                      <mat-icon>business_off</mat-icon>
                      <p>No companies found</p>
                      <small *ngIf="input.value">No results for "{{input.value}}"</small>
                    </div>
                  </td>
                </tr>
              </table>
            </div>

            <mat-paginator [pageSizeOptions]="[5, 10, 25, 50]" 
                          [pageSize]="10" 
                          showFirstLastButtons
                          aria-label="Select page of companies">
            </mat-paginator>
          </mat-card-content>
        </mat-card>
      </div>
    </mat-tab>

    <!-- System Settings Tab -->
    <mat-tab label="System Settings">
      <div class="tab-content">
        <!-- Site Configuration Form -->
        <mat-card class="form-card site-config-form-card" *ngIf="showSiteConfigForm">
          <mat-card-header>
            <mat-card-title>
              <mat-icon>{{selectedSiteConfig ? 'edit' : 'add'}}</mat-icon>
              {{selectedSiteConfig ? 'Edit Site Configuration' : 'Add New Site Configuration'}}
            </mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <form (ngSubmit)="onSubmitSiteConfig()" [formGroup]="siteConfigForm" class="sleek-form">
              <div class="form-section">
                <h3 class="section-title">
                  <mat-icon>settings</mat-icon>
                  Site Configuration Details
                </h3>

                <div class="form-grid">
                  <div class="form-field">
                    <label>Location <span class="required">*</span></label>
                    <input type="text" formControlName="location" 
                           class="form-input"
                           [class.invalid]="scf.location.invalid && scf.location.touched"
                           placeholder="e.g., curacao, aruba, default"
                           [readonly]="selectedSiteConfig !== null">
                    <span class="error-message" *ngIf="scf.location.invalid && scf.location.touched">
                      Location is required (min 2 characters)
                    </span>
                    <small class="form-hint">Unique identifier for this location/use case</small>
                  </div>

                  <div class="form-field">
                    <label>Site Name <span class="required">*</span></label>
                    <input type="text" formControlName="siteName" 
                           class="form-input"
                           [class.invalid]="scf.siteName.invalid && scf.siteName.touched"
                           placeholder="e.g., Curacao Booking">
                    <span class="error-message" *ngIf="scf.siteName.invalid && scf.siteName.touched">
                      Site name is required (min 2 characters)
                    </span>
                  </div>

                  <div class="form-field full-width">
                    <label>Description</label>
                    <textarea formControlName="description" 
                              class="form-input form-textarea"
                              rows="3"
                              placeholder="Site description..."></textarea>
                  </div>

                  <div class="form-field">
                    <label>Primary Color</label>
                    <div class="color-input-group">
                      <input type="color" formControlName="primaryColor" class="color-picker">
                      <input type="text" formControlName="primaryColor" 
                             class="form-input"
                             placeholder="#000000">
                    </div>
                  </div>

                  <div class="form-field">
                    <label>Secondary Color</label>
                    <div class="color-input-group">
                      <input type="color" formControlName="secondaryColor" class="color-picker">
                      <input type="text" formControlName="secondaryColor" 
                             class="form-input"
                             placeholder="#000000">
                    </div>
                  </div>

                  <div class="form-field full-width">
                    <label class="checkbox-label">
                      <input type="checkbox" formControlName="active" class="checkbox-input">
                      <span>Set as active configuration</span>
                    </label>
                    <small class="form-hint">Only one configuration can be active at a time</small>
                  </div>
                </div>
              </div>

              <!-- Image Uploads Section -->
              <div class="form-section">
                <h3 class="section-title">
                  <mat-icon>image</mat-icon>
                  Images
                </h3>

                <div class="form-grid">
                  <div class="form-field full-width">
                    <label>Logo Image</label>
                    <div class="file-upload-area">
                      <input type="file" 
                             accept="image/*" 
                             (change)="onLogoFileSelected($event)"
                             class="file-input"
                             id="logoFile">
                      <label for="logoFile" class="file-input-label">
                        <mat-icon>cloud_upload</mat-icon>
                        {{logoFile ? logoFile.name : 'Choose Logo Image'}}
                      </label>
                      @if (logoPreview) {
                        <div class="image-preview">
                          <img [src]="logoPreview" alt="Logo preview">
                        </div>
                      }
                    </div>
                    <small class="form-hint">Recommended size: 200x50px or similar aspect ratio</small>
                  </div>

                  <div class="form-field full-width">
                    <label>Header/Banner Image</label>
                    <div class="file-upload-area">
                      <input type="file" 
                             accept="image/*" 
                             (change)="onHeaderImageFileSelected($event)"
                             class="file-input"
                             id="headerImageFile">
                      <label for="headerImageFile" class="file-input-label">
                        <mat-icon>cloud_upload</mat-icon>
                        {{headerImageFile ? headerImageFile.name : 'Choose Header Image'}}
                      </label>
                      @if (headerImagePreview) {
                        <div class="image-preview">
                          <img [src]="headerImagePreview" alt="Header preview">
                        </div>
                      }
                    </div>
                    <small class="form-hint">Optional banner image for header</small>
                  </div>

                  <div class="form-field full-width">
                    <label>Favicon</label>
                    <div class="file-upload-area">
                      <input type="file" 
                             accept="image/*" 
                             (change)="onFaviconFileSelected($event)"
                             class="file-input"
                             id="faviconFile">
                      <label for="faviconFile" class="file-input-label">
                        <mat-icon>cloud_upload</mat-icon>
                        {{faviconFile ? faviconFile.name : 'Choose Favicon'}}
                      </label>
                      @if (faviconPreview) {
                        <div class="image-preview">
                          <img [src]="faviconPreview" alt="Favicon preview">
                        </div>
                      }
                    </div>
                    <small class="form-hint">Recommended size: 32x32px or 16x16px</small>
                  </div>
                </div>
              </div>

              <!-- Main Page Hero Media -->
              <div class="form-section">
                <h3 class="section-title">
                  <mat-icon>slideshow</mat-icon>
                  Main Page Hero Media
                </h3>

                <div class="form-grid">
                  <div class="form-field full-width">
                    <label>Home hero headline</label>
                    <input type="text"
                           formControlName="heroHeadline"
                           class="form-input"
                           placeholder="e.g., Find Your Adventure in Curacao!">
                    <small class="form-hint">Optional. When empty, the home page uses Site Name to build the headline (e.g. Find Your Adventure in Curacao!).</small>
                  </div>

                  <div class="form-field full-width">
                    <label>Main Hero Image</label>
                    <div class="file-upload-area">
                      <input type="file"
                             accept="image/*"
                             (change)="onHeroImageFileSelected($event)"
                             class="file-input"
                             id="heroImageFile">
                      <label for="heroImageFile" class="file-input-label">
                        <mat-icon>cloud_upload</mat-icon>
                        {{heroImageFile ? heroImageFile.name : 'Choose Hero Image'}}
                      </label>
                      @if (heroImagePreview) {
                        <div class="image-preview">
                          <img [src]="heroImagePreview" alt="Hero image preview">
                        </div>
                      }
                    </div>
                    <small class="form-hint">Used when no hero video is set</small>
                  </div>

                  <div class="form-field full-width">
                    <label>Main Hero Video</label>
                    <div class="file-upload-area">
                      <input type="file"
                             accept="video/*"
                             (change)="onHeroVideoFileSelected($event)"
                             class="file-input"
                             id="heroVideoFile">
                      <label for="heroVideoFile" class="file-input-label">
                        <mat-icon>cloud_upload</mat-icon>
                        {{heroVideoFile ? heroVideoFile.name : 'Choose Hero Video'}}
                      </label>
                      @if (heroVideoPreview) {
                        <div class="video-preview">
                          <video [src]="heroVideoPreview" controls muted playsinline></video>
                        </div>
                      }
                    </div>
                    <small class="form-hint">Overrides hero image when provided</small>
                  </div>
                </div>
              </div>

              <!-- Category Images -->
              <div class="form-section">
                <h3 class="section-title">
                  <mat-icon>category</mat-icon>
                  Category Images
                </h3>
                <p class="section-description">Exactly {{ CATEGORY_SLOT_COUNT }} categories (one per main-page tile). Choose a preset or Custom, then upload an image.</p>

                <div class="form-grid">
                  @for (idx of categorySlotIndexes; track idx) {
                    <div class="form-field full-width category-slot-row">
                      <label>Category {{ idx + 1 }}</label>
                      <div class="category-name-row">
                        <mat-form-field appearance="outline" class="category-preset-field">
                          <mat-label>Name</mat-label>
                          <mat-select
                            [value]="categorySlotSelectValue[idx]"
                            (selectionChange)="onCategorySlotSelectChange(idx, $event.value)">
                            @for (preset of categoryNamePresets; track preset) {
                              <mat-option [value]="preset">{{ preset }}</mat-option>
                            }
                            <mat-option [value]="CUSTOM_CATEGORY_VALUE">Custom…</mat-option>
                          </mat-select>
                        </mat-form-field>
                        @if (categorySlotSelectValue[idx] === CUSTOM_CATEGORY_VALUE) {
                          <input
                            type="text"
                            class="form-input category-custom-input"
                            [value]="categorySlotCustomName[idx]"
                            (input)="onCategorySlotCustomNameInput(idx, $any($event.target).value)"
                            placeholder="Enter category name"
                            maxlength="80">
                        }
                      </div>
                      <div class="file-upload-area">
                        <input type="file"
                               accept="image/*"
                               (change)="onCategoryImageFileSelected($event, idx)"
                               class="file-input"
                               id="categoryImageFile{{idx}}">
                        <label for="categoryImageFile{{idx}}" class="file-input-label">
                          <mat-icon>cloud_upload</mat-icon>
                          {{ categoryImageFilesBySlot[idx]?.name
                              ? categoryImageFilesBySlot[idx]!.name
                              : ('Choose image for ' + (getResolvedCategoryNameForSlot(idx) || 'category')) }}
                        </label>
                        @if (categoryImagePreviewsBySlot[idx]) {
                          <div class="image-preview">
                            <img [src]="categoryImagePreviewsBySlot[idx]!" [alt]="getResolvedCategoryNameForSlot(idx) + ' preview'">
                          </div>
                        }
                      </div>
                    </div>
                  }
                </div>
              </div>

              <div class="form-actions">
                <button type="submit" 
                        class="btn btn-primary" 
                        [disabled]="siteConfigForm.invalid || isCreatingSiteConfig">
                  <mat-icon>{{selectedSiteConfig ? 'save' : 'add'}}</mat-icon>
                  {{isCreatingSiteConfig ? 'Saving...' : (selectedSiteConfig ? 'Update Configuration' : 'Create Configuration')}}
                </button>
                <button type="button" class="btn btn-secondary" (click)="cancelSiteConfigForm()">
                  <mat-icon>close</mat-icon>
                  Cancel
                </button>
              </div>
            </form>
          </mat-card-content>
        </mat-card>

        <!-- Site Configurations List -->
        <mat-card class="list-card">
          <mat-card-header>
            <mat-card-title>
              <mat-icon>settings</mat-icon>
              Site Configurations
            </mat-card-title>
            <div class="header-actions">
              <button mat-raised-button color="primary" (click)="addSiteConfiguration()">
                <mat-icon>add</mat-icon>
                Add Configuration
              </button>
            </div>
          </mat-card-header>
          <mat-card-content>
            <div class="table-container">
              <table mat-table [dataSource]="siteConfigDataSource" [trackBy]="trackBySiteConfigId" class="admin-table">
                <ng-container matColumnDef="location">
                  <th mat-header-cell *matHeaderCellDef>Location</th>
                  <td mat-cell *matCellDef="let row">
                    <strong>{{row.location}}</strong>
                  </td>
                </ng-container>

                <ng-container matColumnDef="siteName">
                  <th mat-header-cell *matHeaderCellDef>Site Name</th>
                  <td mat-cell *matCellDef="let row">{{row.siteName}}</td>
                </ng-container>

                <ng-container matColumnDef="active">
                  <th mat-header-cell *matHeaderCellDef>Active</th>
                  <td mat-cell *matCellDef="let row">
                    @if (row.active) {
                      <span class="badge badge-success">
                        <mat-icon>check_circle</mat-icon>
                        Active
                      </span>
                    } @else {
                      <span class="badge badge-inactive">
                        <mat-icon>radio_button_unchecked</mat-icon>
                        Inactive
                      </span>
                    }
                  </td>
                </ng-container>

                <ng-container matColumnDef="logo">
                  <th mat-header-cell *matHeaderCellDef>Logo</th>
                  <td mat-cell *matCellDef="let row">
                    @if (row.logoUrl) {
                      <img [src]="row.logoUrl" alt="Logo" class="logo-thumbnail">
                    } @else {
                      <span class="no-image">No logo</span>
                    }
                  </td>
                </ng-container>

                <ng-container matColumnDef="actions">
                  <th mat-header-cell *matHeaderCellDef>Actions</th>
                  <td mat-cell *matCellDef="let row">
                    <button mat-icon-button [matMenuTriggerFor]="siteConfigMenu" 
                            (click)="selectedSiteConfig = row">
                      <mat-icon>more_vert</mat-icon>
                    </button>
                    <mat-menu #siteConfigMenu="matMenu">
                      <button mat-menu-item (click)="editSiteConfiguration(row)">
                        <mat-icon>edit</mat-icon>
                        Edit
                      </button>
                      @if (!row.active) {
                        <button mat-menu-item (click)="setActiveSiteConfiguration(row)">
                          <mat-icon>check_circle</mat-icon>
                          Set Active
                        </button>
                      }
                      <button mat-menu-item (click)="deleteSiteConfiguration(row)" class="delete-action">
                        <mat-icon>delete</mat-icon>
                        Delete
                      </button>
                    </mat-menu>
                  </td>
                </ng-container>

                <tr mat-header-row *matHeaderRowDef="siteConfigDisplayedColumns"></tr>
                <tr mat-row *matRowDef="let row; columns: siteConfigDisplayedColumns;" 
                    class="table-row"></tr>

                <tr class="mat-row" *matNoDataRow>
                  <td class="mat-cell no-data" [attr.colspan]="siteConfigDisplayedColumns.length">
                    <div class="no-data-content">
                      <mat-icon>settings</mat-icon>
                      <p>No site configurations found</p>
                      <button mat-button color="primary" (click)="addSiteConfiguration()">
                        Create First Configuration
                      </button>
                    </div>
                  </td>
                </tr>
              </table>
            </div>
          </mat-card-content>
        </mat-card>
      </div>
    </mat-tab>

    <!-- Hotel Partners Tab -->
    <mat-tab label="Hotel Partners">
      <div class="tab-content">
        <mat-card class="form-card hotel-partner-form-card">
          <mat-card-header>
            <mat-card-title>
              <mat-icon>{{selectedHotelPartner ? 'edit' : 'hotel'}}</mat-icon>
              {{selectedHotelPartner ? 'Edit Hotel Partner' : 'Add Hotel Partner'}}
            </mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <form (ngSubmit)="onSubmitHotelPartner()" [formGroup]="hotelPartnerForm" class="sleek-form">
              <div class="form-section">
                <h3 class="section-title">
                  <mat-icon>handshake</mat-icon>
                  Partner Details
                </h3>

                <div class="form-grid">
                  <div class="form-field">
                    <label>Hotel Name <span class="required">*</span></label>
                    <input type="text"
                           formControlName="name"
                           class="form-input"
                           [class.invalid]="hpf.name.invalid && hpf.name.touched"
                           placeholder="e.g., Ocean View Resort">
                    <span class="error-message" *ngIf="hpf.name.invalid && hpf.name.touched">
                      Hotel name is required
                    </span>
                  </div>

                  <div class="form-field">
                    <label>Referral Code <span class="required">*</span></label>
                    <input type="text"
                           formControlName="code"
                           class="form-input"
                           [class.invalid]="hpf.code.invalid && hpf.code.touched"
                           placeholder="e.g., OCEANVIEW">
                    <small class="form-hint">Use this in booking links as ?hotel=OCEANVIEW</small>
                    <span class="error-message" *ngIf="hpf.code.invalid && hpf.code.touched">
                      Referral code is required
                    </span>
                  </div>

                  <div class="form-field">
                    <label>Site / Market <span class="required">*</span></label>
                    <input type="text"
                           formControlName="siteLocation"
                           class="form-input"
                           [class.invalid]="hpf.siteLocation.invalid && hpf.siteLocation.touched"
                           placeholder="curacao, aruba, bonaire">
                    <span class="error-message" *ngIf="hpf.siteLocation.invalid && hpf.siteLocation.touched">
                      Site / market is required
                    </span>
                  </div>

                  <div class="form-field">
                    <label>Commission %</label>
                    <input type="number"
                           formControlName="commissionRate"
                           class="form-input"
                           min="0"
                           max="100"
                           step="0.01">
                  </div>

                  <div class="form-field">
                    <label>Contact Name</label>
                    <input type="text" formControlName="contactName" class="form-input" placeholder="Front desk or manager">
                  </div>

                  <div class="form-field">
                    <label>Contact Email</label>
                    <input type="email"
                           formControlName="contactEmail"
                           class="form-input"
                           [class.invalid]="hpf.contactEmail.invalid && hpf.contactEmail.touched"
                           placeholder="concierge@hotel.com">
                    <span class="error-message" *ngIf="hpf.contactEmail.invalid && hpf.contactEmail.touched">
                      Enter a valid email address
                    </span>
                  </div>

                  <div class="form-field">
                    <label>Contact Phone</label>
                    <input type="tel" formControlName="contactPhone" class="form-input" placeholder="+1 (555) 123-4567">
                  </div>

                  <div class="form-field">
                    <label class="checkbox-label">
                      <input type="checkbox" formControlName="active" class="checkbox-input">
                      <span>Active referral partner</span>
                    </label>
                  </div>

                  <div class="form-field full-width">
                    <label>Address</label>
                    <input type="text" formControlName="address" class="form-input" placeholder="Hotel address">
                  </div>

                  <div class="form-field full-width">
                    <label>Internal Notes</label>
                    <textarea formControlName="notes"
                              class="form-input form-textarea"
                              rows="3"
                              placeholder="Commission terms, concierge contact preferences, payout notes..."></textarea>
                  </div>
                </div>
              </div>

              <div class="form-actions">
                <button type="submit" class="btn btn-primary" [disabled]="hotelPartnerForm.invalid || isLoading">
                  <mat-icon>{{selectedHotelPartner ? 'save' : 'add'}}</mat-icon>
                  {{selectedHotelPartner ? 'Update Hotel' : 'Create Hotel'}}
                </button>
                <button type="button" class="btn btn-secondary" (click)="resetHotelPartnerForm()">
                  <mat-icon>refresh</mat-icon>
                  Reset
                </button>
              </div>
            </form>
          </mat-card-content>
        </mat-card>

        <mat-card class="list-card">
          <mat-card-header>
            <mat-card-title>
              <mat-icon>hotel</mat-icon>
              Hotel Partner Directory
            </mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <div class="table-container">
              <table mat-table [dataSource]="hotelPartnerDataSource" class="admin-table">
                <ng-container matColumnDef="code">
                  <th mat-header-cell *matHeaderCellDef>Code</th>
                  <td mat-cell *matCellDef="let row"><strong>{{row.code || 'N/A'}}</strong></td>
                </ng-container>

                <ng-container matColumnDef="name">
                  <th mat-header-cell *matHeaderCellDef>Hotel</th>
                  <td mat-cell *matCellDef="let row">{{row.name || 'N/A'}}</td>
                </ng-container>

                <ng-container matColumnDef="siteLocation">
                  <th mat-header-cell *matHeaderCellDef>Site</th>
                  <td mat-cell *matCellDef="let row">{{row.siteLocation || 'default'}}</td>
                </ng-container>

                <ng-container matColumnDef="contact">
                  <th mat-header-cell *matHeaderCellDef>Contact</th>
                  <td mat-cell *matCellDef="let row">
                    <div>{{row.contactName || 'N/A'}}</div>
                    @if (row.contactEmail) {
                      <a [href]="'mailto:' + row.contactEmail" class="email-link">{{row.contactEmail}}</a>
                    }
                  </td>
                </ng-container>

                <ng-container matColumnDef="commissionRate">
                  <th mat-header-cell *matHeaderCellDef>Commission</th>
                  <td mat-cell *matCellDef="let row">{{row.commissionRate || 0}}%</td>
                </ng-container>

                <ng-container matColumnDef="active">
                  <th mat-header-cell *matHeaderCellDef>Status</th>
                  <td mat-cell *matCellDef="let row">
                    @if (row.active !== false) {
                      <span class="badge badge-success">
                        <mat-icon>check_circle</mat-icon>
                        Active
                      </span>
                    } @else {
                      <span class="badge badge-inactive">
                        <mat-icon>pause_circle</mat-icon>
                        Inactive
                      </span>
                    }
                  </td>
                </ng-container>

                <ng-container matColumnDef="actions">
                  <th mat-header-cell *matHeaderCellDef>Actions</th>
                  <td mat-cell *matCellDef="let row">
                    <button mat-icon-button [matMenuTriggerFor]="hotelPartnerMenu"
                            (click)="selectedHotelPartner = row">
                      <mat-icon>more_vert</mat-icon>
                    </button>
                    <mat-menu #hotelPartnerMenu="matMenu">
                      <button mat-menu-item (click)="editHotelPartner(row)">
                        <mat-icon>edit</mat-icon>
                        Edit
                      </button>
                      <button mat-menu-item (click)="deleteHotelPartner(row)" class="delete-action">
                        <mat-icon>delete</mat-icon>
                        Delete
                      </button>
                    </mat-menu>
                  </td>
                </ng-container>

                <tr mat-header-row *matHeaderRowDef="hotelPartnerDisplayedColumns"></tr>
                <tr mat-row *matRowDef="let row; columns: hotelPartnerDisplayedColumns;" class="table-row"></tr>

                <tr class="mat-row" *matNoDataRow>
                  <td class="mat-cell no-data" [attr.colspan]="hotelPartnerDisplayedColumns.length">
                    <div class="no-data-content">
                      <mat-icon>hotel</mat-icon>
                      <p>No hotel partners found</p>
                    </div>
                  </td>
                </tr>
              </table>
            </div>
          </mat-card-content>
        </mat-card>
      </div>
    </mat-tab>
  </mat-tab-group>
</div>

<!-- Loading Overlay -->
<div class="loading-overlay" *ngIf="isLoading">
  <mat-spinner></mat-spinner>
  <p>Loading...</p>
</div>

<!-- Success/Error Messages -->
<!-- Messages are handled via MatSnackBar service in the component -->
